import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Container, PageTitle, NbCard, NbButton, Chip } from "@/components/nb";
import { Stagger, Item } from "@/components/motion";
import { Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import StudyPlan from "@/components/StudyPlan";

export function StudyPlansList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = () => api.get("/study/plans").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const del = async (id) => {
    await api.delete(`/study/plans/${id}`);
    toast.success("Plan deleted");
    load();
  };

  const progressOf = (p) => {
    let all = 0, done = 0;
    p.days.forEach((d) => d.activities.forEach((_, idx) => { all += 1; if (p.progress?.[`${d.day}-${idx}`]) done += 1; }));
    return { done, all, pct: all ? Math.round(100 * done / all) : 0 };
  };

  return (
    <Container>
      <PageTitle eyebrow="STUDY PLANS" title="Your saved 3-day plans." sub="Tick things off, feel the momentum." />
      {loading && <NbCard>Loading...</NbCard>}
      {!loading && items.length === 0 && (
        <NbCard color="tertiary" className="!p-8" data-testid="empty-plans">
          <Sparkles className="w-6 h-6 mb-2" />
          <div className="font-heading font-black text-2xl tracking-tight">No plans yet.</div>
          <p className="text-neutral-800 mt-2">Take a diagnostic and generate a 3-day plan from the result screen.</p>
          <div className="mt-4"><Link to="/study/diagnostic"><NbButton color="black">Start a diagnostic →</NbButton></Link></div>
        </NbCard>
      )}

      <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="plans-list">
        {items.map((p) => {
          const t = progressOf(p);
          return (
            <Item key={p.id}>
              <NbCard className="!p-5 h-full flex flex-col gap-3" data-testid={`plan-card-${p.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Chip color="primary">{t.pct}% DONE</Chip>
                  <span className="font-mono-sl text-[10px] uppercase text-neutral-500">{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
                <h3 className="font-heading font-black text-2xl tracking-tight">{p.title}</h3>
                <p className="text-neutral-700 italic text-sm">"{p.summary}"</p>
                <div className="h-2 w-full bg-white nb-border rounded-full overflow-hidden">
                  <div className="h-full bg-sl-primary" style={{ width: `${t.pct}%` }} />
                </div>
                <div className="font-mono-sl text-[10px] uppercase text-neutral-600">{t.done} of {t.all} tasks done</div>
                <div className="flex gap-2 mt-auto flex-wrap">
                  <NbButton onClick={() => nav(`/study/plans/${p.id}`)} color="primary" data-testid={`open-${p.id}`}>Open plan</NbButton>
                  <NbButton onClick={() => del(p.id)} color="white" data-testid={`del-plan-${p.id}`}><Trash2 className="w-4 h-4" /></NbButton>
                </div>
              </NbCard>
            </Item>
          );
        })}
      </Stagger>
    </Container>
  );
}

export function StudyPlanDetail() {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/study/plans/${id}`).then((r) => setPlan(r.data)).catch(() => setErr("Plan not found"));
  }, [id]);

  if (err) return <Container><NbCard color="tertiary">{err}</NbCard></Container>;
  if (!plan) return <Container><div>Loading...</div></Container>;

  return (
    <Container>
      <div className="mb-2">
        <Link to="/study/plans" data-testid="back-plans" className="font-mono-sl text-xs uppercase underline">← All plans</Link>
      </div>
      <StudyPlan
        subject={plan.subject}
        level={plan.level}
        pct={0}
        weak_topics={[]}
        initialPlan={plan}
      />
    </Container>
  );
}
